import React, {
  useState,
} from "react";
import logo from "../assets/logo.png";
import {
  addClient,
  uploadPolicyDocument,
} from "../api";

import {
  ShieldCheck,
  FileUp,
  CheckCircle2,
  X,
  Car,
  FileText,
  BadgeDollarSign,
  Building2,
  CalendarClock,
  UploadCloud,
  LoaderCircle,
} from "lucide-react";

import {
  toast,
} from "react-toastify";

import "../styles/NewClientModal.css";

const NewClientModal = ({
  onClose,
  refreshClients,
}) => {

  /* =========================================
      STATE
  ========================================= */

  const [clientData,
    setClientData,
  ] = useState({

    name: "",

    policyNumber: "",

    vehicleNumber: "",

    premiumPaid: "No",

    paidToApex: "No",

    paymentNumber: "",

    premium: "",

    insurer: "",

    renewalDate: "",

    policyType: "Motor",

    policyDocument: null,

  });

  const [isUploading,
    setIsUploading,
  ] = useState(false);

  const [saving,
    setSaving,
  ] = useState(false);

  /* =========================================
      CHANGE
  ========================================= */

  const handleChange =
    (e) => {

      setClientData({

        ...clientData,

        [e.target.name]:
          e.target.value,

      });
    };

  /* =========================================
      FILE UPLOAD
  ========================================= */

  const handleFileChange =
    async (e) => {

      const file =
        e.target.files[0];

      if (!file)
        return;

      try {

        setIsUploading(true);

        const uploadedFileUrl =
          await uploadPolicyDocument(
            file
          );

        setClientData({

          ...clientData,

          policyDocument:
            uploadedFileUrl,

        });

        toast.success(
          "Document uploaded successfully."
        );

      } catch (error) {

        console.error(error);

        toast.error(
          "Upload failed."
        );

      } finally {

        setIsUploading(
          false
        );

      }

    };

  /* =========================================
      SUBMIT
  ========================================= */

const handleSubmit =
  async () => {

    try {

      setSaving(true);

      /* ===================================
          FORCE LOADER DISPLAY
      =================================== */

      const savePromise =
        addClient(
          clientData
        );

      /* ===================================
          MINIMUM 15 SECOND LOADER
      =================================== */

      await Promise.all([

        savePromise,

        new Promise(
          (resolve) =>
            setTimeout(
              resolve,
              15000
            )
        ),

      ]);

      await refreshClients();

      toast.success(
        "Client added successfully."
      );

      setTimeout(() => {

        onClose();

      }, 1200);

    } catch (error) {

      console.error(error);

      toast.error(
        "Failed to add client."
      );

    } finally {

      setSaving(false);

    }

  };

  /* =========================================
      SWITCH
  ========================================= */

  const Switch = ({
    name,
    checked,
    onChange,
  }) => (

    <label className="insurance-modern-switch">

      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
      />

      <span className="insurance-modern-slider" />

      <span className="insurance-modern-switch-text">

        {checked
          ? "YES"
          : "NO"}

      </span>

    </label>
  );

  /* =========================================
      RENDER
  ========================================= */

  return (

    <div className="insurance-modern-modal-overlay">

      <div className="insurance-modern-modal">

        {/* CLOSE */}

        <button
          className="insurance-modern-close"
          onClick={onClose}
        >

          <X size={18} />

        </button>

        {/* LEFT PANEL */}

        <div className="insurance-modern-modal-left">

          <div className="insurance-modern-policy-badge">

            <ShieldCheck
              size={15}
            />

            POLICY REGISTRATION

          </div>

          <h1>

            New
            <br />

            Insurance
            <br />

            Client

          </h1>

          <p>

          Client Onboarding, Please Ensure Accurate
          Information before Submitting Application.
          </p>

          {/* LIVE PREVIEW */}

          <div className="insurance-modern-preview-card">

            <div className="insurance-modern-preview-header">

              <div className="insurance-modern-preview-icon">

                <ShieldCheck
                  size={20}
                />

              </div>

              <div>

                <h3>

                  {
                    clientData.name ||
                    "Client Name"
                  }

                </h3>

                <span>

                  {
                    clientData.insurer ||
                    "Insurance Company"
                  }

                </span>

              </div>

            </div>

            <div className="insurance-modern-preview-body">

              <div>

                <Car
                  size={15}
                />

                {
                  clientData.vehicleNumber ||
                  "Vehicle Number"
                }

              </div>

              <div>

                <FileText
                  size={15}
                />

                {
                  clientData.policyNumber ||
                  "Policy Number"
                }

              </div>

              <div>

                <BadgeDollarSign
                  size={15}
                />

                $
                {
                  clientData.premium ||
                  "0.00"
                }

              </div>

              <div>

                <CalendarClock
                  size={15}
                />

                {
                  clientData.renewalDate ||
                  "Renewal Date"
                }

              </div>

            </div>

          </div>

        </div>

        {/* RIGHT */}

        <div className="insurance-modern-modal-right">

          {/* HEADER */}

          <div className="insurance-modern-modal-header">

            <h2>

              Insurance
              Onboarding

            </h2>

            <p>

              Complete the
              client policy
              information below. 
              Ensure Information are Accurate!

            </p>

          </div>

          {/* FORM */}

          <div className="insurance-modern-form-grid">

            {/* CLIENT */}

            <div className="insurance-modern-field">

              <label>
                Client Name
              </label>

              <input
                type="text"
                name="name"
                value={
                  clientData.name
                }
                onChange={
                  handleChange
                }
                placeholder="Enter client name"
              />

            </div>

            <div className="insurance-modern-field">

              <label>
                Policy Number
              </label>

              <input
                type="text"
                name="policyNumber"
                value={
                  clientData.policyNumber
                }
                onChange={
                  handleChange
                }
                placeholder="Policy number"
              />

            </div>

            <div className="insurance-modern-field">

              <label>
                Vehicle Number
              </label>

              <input
                type="text"
                name="vehicleNumber"
                value={
                  clientData.vehicleNumber
                }
                onChange={
                  handleChange
                }
                placeholder="Vehicle number"
              />

            </div>

            <div className="insurance-modern-field">

              <label>
                Insurer
              </label>

              <input
                type="text"
                name="insurer"
                value={
                  clientData.insurer
                }
                onChange={
                  handleChange
                }
                placeholder="Insurance company"
              />

            </div>

            {/* PAYMENT */}

            <div className="insurance-modern-field">

              <label>
                Premium
              </label>

              <input
                type="text"
                name="premium"
                value={
                  clientData.premium
                }
                onChange={
                  handleChange
                }
                placeholder="Premium amount"
              />

            </div>

            <div className="insurance-modern-field">

              <label>
                Payment Number
              </label>

              <input
                type="text"
                name="paymentNumber"
                value={
                  clientData.paymentNumber
                }
                onChange={
                  handleChange
                }
                placeholder="Payment reference"
              />

            </div>

            {/* TOGGLES */}

            <div className="insurance-modern-toggle-card">

              <div>

                <h4>
                  Premium Paid
                </h4>

                <p>

                  Mark if client
                  completed
                  premium payment.

                </p>

              </div>

              <Switch
                name="premiumPaid"
                checked={
                  clientData.premiumPaid ===
                  "Yes"
                }
                onChange={(e) =>
                  setClientData({

                    ...clientData,

                    premiumPaid:
                      e.target.checked
                        ? "Yes"
                        : "No",

                  })
                }
              />

            </div>

            <div className="insurance-modern-toggle-card">

              <div>

                <h4>
                  Paid To Apex
                </h4>

                <p>

                  Mark if payment
                  was transferred
                  to Apex.

                </p>

              </div>

              <Switch
                name="paidToApex"
                checked={
                  clientData.paidToApex ===
                  "Yes"
                }
                onChange={(e) =>
                  setClientData({

                    ...clientData,

                    paidToApex:
                      e.target.checked
                        ? "Yes"
                        : "No",

                  })
                }
              />

            </div>

            {/* RENEWAL */}

            <div className="insurance-modern-field">

              <label>
                Renewal Date
              </label>

              <input
                type="date"
                name="renewalDate"
                value={
                  clientData.renewalDate
                }
                onChange={
                  handleChange
                }
              />

            </div>

            <div className="insurance-modern-field">

              <label>
                Policy Type
              </label>

              <select
                name="policyType"
                value={
                  clientData.policyType
                }
                onChange={
                  handleChange
                }
              >

                <option value="Motor">
                  Motor
                </option>

                <option value="Fire">
                  Fire
                </option>

                <option value="Life">
                  Life
                </option>

              </select>

            </div>

          </div>

          {/* UPLOAD */}

          <div className="insurance-modern-upload-card">

            <div className="insurance-modern-upload-header">

              <UploadCloud
                size={20}
              />

              <div>

                <h4>

                  Policy
                  Document

                </h4>

                <p>

                  Upload policy
                  PDF or image.

                </p>

              </div>

            </div>

            <input
              type="file"
              id="policyUpload"
              onChange={
                handleFileChange
              }
              hidden
            />

            <label
              htmlFor="policyUpload"
              className="insurance-modern-upload-btn"
            >

              {isUploading ? (

                <>

                  <LoaderCircle
                    size={18}
                    className="spin"
                  />

                  Uploading...

                </>

              ) : (

                <>

                  <FileUp
                    size={18}
                  />

                  Choose File

                </>

              )}

            </label>

            {clientData.policyDocument && (

              <div className="insurance-modern-upload-success">

                <CheckCircle2
                  size={16}
                />

                Document uploaded
                successfully

              </div>

            )}

          </div>

{/* ACTIONS */}

<div className="insurance-modern-modal-actions">

  <button
    className="insurance-modern-cancel-btn"
    onClick={onClose}
    disabled={saving}
  >

    Cancel

  </button>

  {!saving && (

    <button
      className="insurance-modern-save-btn"
      onClick={handleSubmit}
      disabled={isUploading}
    >

      <Building2 size={18} />

      Register Client

    </button>

  )}

</div>

{/* =====================================
    PROCESSING OVERLAY
===================================== */}

{saving && (

  <div className="insurance-modern-processing-overlay">

    <div className="insurance-modern-processing-loader">

      <div className="insurance-modern-processing-ring" />

      <div className="insurance-modern-processing-ring-two" />

      <div className="insurance-modern-processing-center">

        <img
          src={logo}
          alt="RCS Logo"
        />

      </div>

    </div>

    <h2>
      Registering Policy
    </h2>

    <p>

      Hang Tight, We're Uploading documents,
      validating records
      and onboarding
      insurance client...

    </p>

  </div>

)}

        </div>

      </div>

    </div>
  );
};

export default NewClientModal;